<template>
  <div v-if="isLoading" class="page-loading">
    <b-loading :active.sync="isLoading" is-full-page/>
  </div>
  <div v-else class="declaration-page">

    <!-- Top bar -->
    <div class="page-topbar">
      <language-switcher/>
    </div>

    <!-- Valid token: main form or success -->
    <div v-if="!errorMessage" class="page-body">

      <!-- Hero -->
      <div class="declaration-hero">
        <h1 class="declaration-hero__title">
          {{ $t('consignee.consignee_declaration.welcome') }}
        </h1>
        <p class="declaration-hero__name">{{ declaration_mail.enduserName }}</p>
        <p class="declaration-hero__address">
          {{ [declaration_mail.address, declaration_mail.city, declaration_mail.country, declaration_mail.postalCode].filter(Boolean).join(' ') }}
        </p>
      </div>

      <!-- Crates PDF button -->
      <div class="has-text-centered mt-5 mb-4">
        <b-button type="is-info" icon-left="download" @click="downloadPDF">
          {{ $t('consignee.consignee_declaration.crates_identification') }}
        </b-button>
      </div>

      <!-- Declaration form -->
      <div v-if="!submited" class="declaration-form-wrap">
        <ValidationObserver v-slot="{ invalid }">

          <h2 class="declaration-section-title">
            {{ $t('consignee.consignee_declaration.title') }}
          </h2>
          <p class="declaration-section-sub">
            {{ $t('consignee.consignee_declaration.sub-title') }}
          </p>

          <!-- Material quantity cards -->
          <div class="material-grid">
            <div v-for="dc of declarationQuantities" :key="dc.material" class="material-card">
              <div class="material-card__header">
                <span class="material-card__name">{{ dc.material }}</span>
                <span v-if="dc.material === 'GPS52' || dc.material === 'GPS55'" class="material-card__info">
                  <b-tooltip position="is-bottom" multilined>
                    <b-button
                      icon-left="info-circle"
                      type="is-primary"
                      size="is-small"
                      rounded
                      @click="showModal(dc.material)"
                    />
                    <template v-slot:content>
                      <b-image :src="getImgSrc(dc.material)" :alt="`${dc.material} note`"/>
                      <strong>Click to see more</strong>
                    </template>
                  </b-tooltip>
                </span>
              </div>
              <p v-if="matLibelles && matLibelles.find(row => row.MATNR === dc.material)" class="material-card__label">
                {{ matLibelles.find(row => row.MATNR === dc.material).MAKTX }}
              </p>
              <ValidationProvider v-slot="{errors}" slim :rules="{required: true, maxInput: true, minInput: true}">
                <b-input
                  v-model="dc.quantity"
                  min="0"
                  :name="'quantity' + dc.material"
                  type="number"
                />
                <span class="has-text-danger is-size-7">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <b-modal :active.sync="isModalVisible" @close="hideModal">
            <p class="image">
              <img :src="getImgSrc(currentMaterial)">
            </p>
          </b-modal>

          <!-- Comments -->
          <div class="declaration-field">
            <label class="declaration-field__label">
              {{ $t('consignee.consignee_declaration.comment') }}
            </label>
            <textarea v-model="declaration.comments" class="textarea" rows="3"/>
          </div>

          <!-- Identity -->
          <div class="declaration-field">
            <label class="declaration-field__label">
              {{ $t('consignee.consignee_declaration.identity') }}
            </label>
            <ValidationProvider v-slot="{errors}" slim :rules="{required: true}">
              <b-input v-model="declaration.identity" :name="'identity'"/>
              <span class="has-text-danger is-size-7">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <!-- Submit -->
          <div class="has-text-centered mt-5 mb-6">
            <button :disabled="invalid" class="button is-primary is-medium" @click="submit">
              {{ $t('consignee.consignee_declaration.submit') }}
            </button>
          </div>

        </ValidationObserver>
      </div>

      <!-- Submitted success -->
      <div v-else class="declaration-success">
        <h1 class="has-text-primary title is-4">
          {{ $t('consignee.consignee_declaration.declaration_sent.title') }}
        </h1>
        <p>{{ $t('consignee.consignee_declaration.declaration_sent.content1') }}</p>
        <div class="mt-5">
          <button class="button is-info" :class="{ 'is-loading': downloadingPdf }" @click="getPdfReport()">
            {{ $t('consignee.consignee_declaration.declaration_sent.download_declaration') }}
          </button>
        </div>
      </div>

    </div>

    <!-- Invalid token -->
    <div v-else class="page-body">
      <div v-if="!askedNewMail">
        <div class="declaration-hero is-danger">
          <h1 class="declaration-hero__title" v-if="declaration_mail && declaration_mail.declarationId">
            {{ $t('consignee.consignee_declaration.token_used') }}
          </h1>
          <h1 class="declaration-hero__title" v-else>
            {{ $t('consignee.consignee_declaration.something_went_wrong') }}
          </h1>
        </div>
        <div class="declaration-error-body">
          <p v-if="!(declaration_mail && declaration_mail.declarationId)">{{ errorMessage }}</p>
          <div v-if="invalidToken" class="mt-4">
            <p v-if="declaration_mail && declaration_mail.declarationId">
              {{ $t('consignee.consignee_declaration.token_used_message') }}
            </p>
            <p v-else>
              {{ $t('consignee.consignee_declaration.ask_new_mail_message') }}
            </p>
            <div class="mt-4">
              <button class="button is-info" @click="askNewMail">
                {{ $t('consignee.consignee_declaration.ask_new_mail_button') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="declaration-hero">
          <h1 class="declaration-hero__title">{{ $t('global.success') }}</h1>
        </div>
        <div class="declaration-error-body">
          <p>{{ $t('consignee.consignee_declaration.new_email_sent') }}</p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import EnduserDeclaration from '@/services/v2/enduserDeclaration'
import LanguageSwitcher from '../../components/common/languageSwitcher.vue'
import languageHelper from '../../mixins/misc'
import { saveAs } from 'file-saver'

export default {
  components: {
    LanguageSwitcher
  },
  data () {
    return {
      isModalVisible: false,
      currentMaterial: '',
      declaration_mail: null,
      declaration: Object,
      submited: false,
      token: this.$route.query.token,
      materials: [],
      declarationQuantities: [],
      isLoading: true,
      errorMessage: '',
      downloadingPdf: false,
      matLibelles: null,
      invalidToken: false,
      askedNewMail: false
    }
  },
  mounted: async function () {
    try {
      this.isLoading = true
      this.declaration_mail = await EnduserDeclaration.get(this.token)
      this.$i18n.locale = languageHelper.methods.getLangShortName(this.declaration_mail.lang) || 'en'
      if (this.declaration_mail.declarationId) {
        throw Error('Invalid token')
      }
      this.materials = await EnduserDeclaration.getMaterials(this.token, this.declaration_mail.enduserCode)
      const dqTemp = []
      this.materials.map((m) => {
        if (m.MATERIAL === 'GPS52') {
          dqTemp.push({ material: 'GPS52', quantity: 0 }, { material: 'GPS55', quantity: 0 })
        } else {
          dqTemp.push({ material: m.MATERIAL, quantity: 0 })
        }
      })
      this.declarationQuantities = dqTemp.reduce((acc, cur) => {
        const x = acc.find(item => item.material === cur.material)
        return x ? acc : [...acc, cur]
      }, [])
      this.declaration = {
        id: this.declaration_mail.declarationId,
        enduserCode: this.declaration_mail.enduserCode,
        enduserName: this.declaration_mail.enduserName,
        declarationMailId: this.declaration_mail.id,
        comments: '',
        identity: ''
      }
      this.matLibelles = await EnduserDeclaration.getMatLibelles(this.token)
      this.isLoading = false
    } catch (error) {
      if (error.message === 'Invalid token') {
        this.invalidToken = true
        this.errorMessage = this.$t('consignee.consignee_declaration.invalid_token_message')
      } else {
        this.errorMessage = error.message
      }
      this.isLoading = false
    }
  },
  methods: {
    sendDeclaration: async function () {
      try {
        await EnduserDeclaration.post(this.token, this.declaration, this.declarationQuantities)
        this.submited = true
      } catch (error) {
        this.$buefy.toast.open({ message: error.message || error, type: 'is-danger' })
      }
    },
    submit: async function () {
      try {
        let total = 0.0
        this.declarationQuantities.forEach(function (dc) {
          dc.quantity = parseInt(dc.quantity, 10)
          total += parseInt(dc.quantity)
        })
        this.$buefy.dialog.confirm({
          title: this.$t('consignee.consignee_declaration.modal.title'),
          message: this.$t('consignee.consignee_declaration.modal.content1') + ` <strong>${total}</strong> ` + this.$t('consignee.consignee_declaration.modal.content2'),
          confirmText: this.$t('consignee.consignee_declaration.modal.yes'),
          cancelText: this.$t('consignee.consignee_declaration.modal.no'),
          type: 'is-info',
          hasIcon: true,
          onConfirm: async () => { this.sendDeclaration() }
        })
      } catch (error) {
        this.$buefy.toast.open({ message: error.message || error, type: 'is-danger' })
      }
    },
    getPdfReport: async function () {
      try {
        this.downloadingPdf = true
        await EnduserDeclaration.getPdfReport(this.token)
      } catch (error) {
        this.$buefy.toast.open({ message: error.message || error, type: 'is-danger' })
      }
      this.downloadingPdf = false
    },
    askNewMail: async function () {
      try {
        await EnduserDeclaration.askNewMail(this.token)
        this.askedNewMail = true
      } catch (error) {
        this.$buefy.toast.open({ message: error.message || error, type: 'is-danger' })
      }
    },
    showModal (material) {
      this.currentMaterial = material
      this.isModalVisible = true
    },
    hideModal () {
      this.isModalVisible = false
      this.currentMaterial = ''
    },
    getImgSrc (material) {
      return material ? require(`@/assets/doc/${material}-note.png`) : ''
    },
    async downloadPDF () {
      const result = await EnduserDeclaration.getCrateDifferencePdf()
      const blob = new Blob([result], { type: 'application/pdf' })
      saveAs(blob, 'GPS_crates_note.pdf')
    }
  }
}
</script>

<style scoped>
.declaration-page {
  min-height: 100vh;
  background: #f7f8fa;
}

/* Top bar */
.page-topbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #ebebeb;
  position: sticky;
  top: 0;
  z-index: 50;
}

/* Content body */
.page-body {
  max-width: 860px;
  margin: 0 auto;
  padding: 24px 16px 40px;
}

/* Hero banner */
.declaration-hero {
  background: linear-gradient(135deg, #48c78e 0%, #3abb82 100%);
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  color: #fff;
  margin-bottom: 8px;
}

.declaration-hero.is-danger {
  background: linear-gradient(135deg, #f14668 0%, #cc3358 100%);
}

.declaration-hero__title {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 10px;
  line-height: 1.2;
}

.declaration-hero__name {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 4px;
  opacity: 0.95;
}

.declaration-hero__address {
  font-size: 0.9rem;
  opacity: 0.85;
}

/* Section headings */
.declaration-section-title {
  font-size: 1.25rem;
  font-weight: 700;
  text-align: center;
  margin-top: 32px;
  margin-bottom: 6px;
  color: #363636;
}

.declaration-section-sub {
  text-align: center;
  color: #6b7280;
  margin-bottom: 24px;
  font-size: 0.95rem;
}

/* Material grid */
.material-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 14px;
  margin-bottom: 24px;
}

.material-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px 14px 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.material-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.material-card__name {
  font-weight: 700;
  font-size: 1rem;
  color: #363636;
}

.material-card__label {
  font-size: 0.78rem;
  color: #6b7280;
  margin-bottom: 10px;
  line-height: 1.3;
}

/* Fields */
.declaration-field {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}

.declaration-field__label {
  display: block;
  font-weight: 600;
  font-size: 0.95rem;
  color: #363636;
  margin-bottom: 10px;
}

/* Success / error body */
.declaration-success,
.declaration-error-body {
  text-align: center;
  padding: 32px 16px;
}

/* Mobile overrides */
@media (max-width: 480px) {
  .declaration-hero__title {
    font-size: 1.4rem;
  }

  .material-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
